import {Heart, Lock, Mail, Eye, EyeOff, CircleAlert} from "lucide-react";
import type {FormEvent} from "react";
import type {AuthErrors} from "./types";
import {getLoginAlert} from "./constants";
import {Button, Input} from "../../ui";
import {cn} from "../../ui/core/utils";
import {VKLoginButton} from "../../../components/auth/VKLoginButton";
import type {VKLoginResult} from "../../../components/auth/VKLoginButton";

/** Белое поле формы входа. Собственный язык экрана: не утопленное --rla-sunk. */
const FIELD_BASE =
    "rounded-2xl border-gray-200 bg-white pl-11 shadow-sm text-gray-900 " +
    "placeholder:text-gray-400 focus:border-brand-from focus:ring-1 focus:ring-brand-from";

/**
 * Признак ошибки поля — только цвет рамки, без текста под полем.
 *
 * Дописывается ПОСЛЕ className, а не берётся из `error` у Input: там ring
 * задан `shadow-[inset_…]`, а FIELD_BASE несёт `shadow-sm`, и twMerge (className
 * идёт последним) гасил бы его — на этом экране проп `error` визуально не
 * работал вовсе.
 */
const FIELD_ERROR_RING =
    "shadow-[0_0_0_1.5px_var(--rla-alarm),0_0_0_5px_rgba(179,55,47,0.12)] " +
    "focus:shadow-[0_0_0_1.5px_var(--rla-alarm),0_0_0_5px_rgba(179,55,47,0.12)] " +
    "focus:border-transparent focus:ring-0";

/**
 * Фирменный знак Яндекс ID для кнопки входа: белая «Я» в красном круге
 * (#FC3F1D). Предыдущая иконка рисовала произвольную геометрию и знаком
 * Яндекса не была. Inline-SVG — чтобы не тянуть внешние зависимости.
 */
function YandexIdIcon() {
    return (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
        >
            <circle cx="12" cy="12" r="12" fill="#FC3F1D"/>
            <path
                d="M13.32 20.24h2.69V3.76h-3.91c-3.93 0-6 2.02-6 5 0 2.38 1.13 3.78 3.16 5.23l-3.53 6.25h2.91l3.93-6.87-1.36-.91c-1.65-1.11-2.45-1.97-2.45-3.83 0-1.63 1.15-2.73 3.32-2.73h1.24v14.34z"
                fill="#fff"
            />
        </svg>
    );
}

interface LoginFormProps {
    email: string;
    password: string;
    errors: AuthErrors;
    /**
     * Ответ сервера на неудачный вход. Живёт отдельно от `errors`, потому что
     * относится к паре «логин + пароль» целиком, а не к одному полю: раньше его
     * клали в errors.email И в errors.password сразу, и один и тот же текст
     * печатался под обоими полями. Здесь он рисуется один раз — плашкой сверху,
     * поле пароля получает только кольцо (error), без своей строки.
     */
    formError: string | null;
    /** Запрос входа в полёте — единственная видимая реакция на тап «Войти». */
    submitting: boolean;
    showPassword: boolean;
    onBlur: (field: "email" | "password", value: string) => void;
    onTogglePassword: () => void;
    onSubmit: (e: FormEvent) => void;
    onToggleAuthMode: () => void;
    onOpenReset: () => void;
    onLoginWithYandex: () => void;
    onLoginWithMessenger: () => void;
    onLoginWithVK: (data: VKLoginResult) => void;
    onVKError?: (error: any, phase: "init" | "login") => void;
}

export function LoginForm(
    {
        email,
        password,
        errors,
        formError,
        submitting,
        showPassword,
        onBlur,
        onTogglePassword,
        onSubmit,
        onToggleAuthMode,
        onOpenReset,
        onLoginWithYandex,
        onLoginWithMessenger,
        onLoginWithVK,
        onVKError,
    }: LoginFormProps) {
    const alert = formError ? getLoginAlert(formError) : null;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {alert && (
                <div role="alert" className="rla-formalert">
                    <CircleAlert className="rla-formalert-icon size-5" aria-hidden="true"/>
                    <div className="min-w-0">
                        <p className="rla-formalert-title">{alert.title}</p>
                        {alert.showPasswordHint && (
                            <p className="rla-formalert-hint">
                                Проверьте раскладку и заглавные буквы — или{" "}
                                <button type="button" onClick={onOpenReset} className="rla-formalert-link">
                                    создайте новый пароль
                                </button>
                                .
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Email или телефон</label>
                <div className="relative">
                    <Mail className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-gray-400"/>
                    <Input
                        size="lg"
                        id="login-username"
                        name="username"
                        inputMode="email"
                        autoComplete="username"
                        defaultValue={email}
                        onBlur={(e) => onBlur("email", e.target.value)}
                        placeholder="example@mail.ru или +7…"
                        error={Boolean(errors.email)}
                        className={cn(FIELD_BASE, errors.email && FIELD_ERROR_RING)}
                    />
                </div>
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Пароль</label>
                <div className="relative">
                    <Lock className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-gray-400"/>
                    <Input
                        type={showPassword ? "text" : "password"}
                        size="lg"
                        id="login-password"
                        name="password"
                        defaultValue={password}
                        onBlur={(e) => onBlur("password", e.target.value)}
                        placeholder="••••••••"
                        error={Boolean(errors.password) || Boolean(alert)}
                        className={cn(FIELD_BASE, (errors.password || alert) && FIELD_ERROR_RING)}
                        autoComplete="current-password"
                    />
                    <Button
                        type="button"
                        onClick={onTogglePassword}
                        data-keep-keyboard
                        variant="ghost"
                        size="iconSm"
                        aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                        className="absolute right-1 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center text-gray-400 hover:bg-transparent"
                    >
                        {showPassword ? <EyeOff className="size-5"/> : <Eye className="size-5"/>}
                    </Button>
                </div>
                {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}

                {/* Ссылка на сброс — сразу под полем пароля, прижата вправо */}
                <div className="mt-2 flex justify-end">
                    <Button
                        type="button"
                        onClick={onOpenReset}
                        variant="link"
                        className="h-auto -mx-3 -my-3 px-3 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
                    >
                        Забыли пароль?
                    </Button>
                </div>
            </div>

            <button
                type="submit"
                className="rla-match-cta w-full disabled:opacity-70"
                disabled={submitting}
                aria-busy={submitting}
            >
                {submitting ? (
                    <span className="size-5 animate-spin rounded-full border-[3px] border-white/40 border-t-white"/>
                ) : (
                    <Heart className="size-5"/>
                )}
                {submitting ? "Входим…" : "Войти"}
            </button>

            {/* ── Yandex ID ──────────────────────────────────────────────────── */}
            <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"/>
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="bg-[var(--rla-bg)] px-3 text-gray-400 font-medium">или</span>
                </div>
            </div>

            <button
                type="button"
                onClick={onLoginWithYandex}
                className="rla-yandex-login-btn flex w-full items-center justify-center gap-2.5"
            >
                <YandexIdIcon/>
                <span>Войти через Яндекс</span>
            </button>

            {/* ── VK ID OneTap ───────────────────────────────────────────────── */}
            <div className="mt-2.5">
                <VKLoginButton
                    onSuccess={onLoginWithVK}
                    onError={onVKError}
                />
            </div>

            <div className="pt-6 text-center">
                <p className="text-sm text-gray-600 font-medium">
                    Ещё нет анкеты?{" "}
                    <Button
                        type="button"
                        onClick={onToggleAuthMode}
                        variant="link"
                        className="h-auto -my-3 py-3 px-2 -mx-2 text-gray-600 hover:text-gray-900 font-bold"
                    >
                        Создать
                    </Button>
                </p>
            </div>
        </form>
    );
}